import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "../utils/i18n-config";

const LanguageContext = createContext();

const LANGUAGES = [
  { nativeName: "English", flag: "🇺🇸 ", value: "en" },
  { nativeName: "Français", flag: "🇫🇷 ", value: "fr" },
  { nativeName: "العربية", flag: "🇲🇦 ", value: "ar" },
  { nativeName: "Español", flag: "🇪🇸 ", value: "es" },
  { nativeName: "Italiano", flag: "🇮🇹 ", value: "it" },
];

const CODES = LANGUAGES.map((l) => l.value);

// Reads /fr/privacy-policy and returns { lang: "fr", rest: "/privacy-policy" }.
// A path without a language prefix returns lang null and the path unchanged.
export const splitLanguageFromPath = (pathname) => {
  const match = pathname.match(/^\/([a-z]{2})(\/.*)?$/);
  if (match && CODES.includes(match[1])) {
    return { lang: match[1], rest: match[2] || "/" };
  }
  return { lang: null, rest: pathname };
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const urlLang = splitLanguageFromPath(location.pathname).lang;

  const [language, setLanguage] = useState(
    LANGUAGES.find((l) => l.value === urlLang) || LANGUAGES[0],
  );
  const [languages] = useState(LANGUAGES);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef(null);

  // The URL is the source of truth. This also runs on first mount, so opening
  // /ar/privacy-policy in a fresh tab shows Arabic without touching the picker.
  useEffect(() => {
    const target = urlLang || "en";
    if (i18n.language === target) return;
    const next = LANGUAGES.find((l) => l.value === target);
    if (next) {
      i18n.changeLanguage(next.value);
      setLanguage(next);
    }
  }, [urlLang]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng.value);
    setLanguage(lng);

    const { rest } = splitLanguageFromPath(location.pathname);
    // English is the default and carries no prefix, so its URLs stay clean.
    const nextPath =
      lng.value === "en" ? rest : `/${lng.value}${rest === "/" ? "" : rest}`;
    navigate(`${nextPath}${location.search}${location.hash}`, {
      replace: true,
    });
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  useEffect(() => {
    const isRTL = language.value === "ar";
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", language.value);
  }, [language]);

  // Prefixes a path with the current language. English keeps clean URLs.
  const localePath = (path) =>
    language.value === "en"
      ? path
      : `/${language.value}${path === "/" ? "" : path}`;

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        languages,
        isLanguageDropdownOpen,
        setIsLanguageDropdownOpen,
        toggleLanguageDropdown,
        languageDropdownRef,
        localePath,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
